import React, { useCallback, useMemo, useRef } from 'react';
import { IconUpload, Typography, DesignTokens } from '@klarna/bubble-ui';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { logEvent } from '@/utils/analytics';

import { LinkWithoutHref } from '../LinkWithoutHref';
import {
  ACCEPTED_FORMATS,
  ERROR_CODES,
  MAX_FILE_SIZE_IN_MB,
  MAX_NUMBER_OF_FILES,
  tPrefix,
} from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import { UploadAreaLabel, UploaderContainer } from './styledComponents';

type UploadAreaProps = {
  cyElementId: string;
  documents: StakeholderDocument[];
  errors: string[] | null;
  onUpload: (files: File[]) => void;
  setErrors: (errorIds: string[] | null) => void;
  t: TranslatorFunction;
};

type FileFilterFunction = (files: File[]) => { filteredFiles: File[]; error?: string };

export const UploadArea = ({
  t,
  documents = [],
  onUpload,
  cyElementId,
  errors,
  setErrors,
}: UploadAreaProps): React.JSX.Element => {
  const designContext = DesignTokens.useDesignContext();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFileBrowser = useCallback(() => {
    // eslint-disable-next-line no-unused-expressions
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const removeDuplicateFiles = useCallback<FileFilterFunction>(
    files => {
      const uniqueFiles = files.reduce(
        (acc: File[], selectedFile) =>
          documents.findIndex(doc => doc.filename === selectedFile.name) === -1
            ? [...acc, selectedFile]
            : acc,
        [],
      );
      return {
        filteredFiles: uniqueFiles,
        error: uniqueFiles.length < files.length ? ERROR_CODES.DUPLICATE_FILE_NAME : undefined,
      };
    },
    [documents],
  );

  const removeInvalidFileFormats = useCallback<FileFilterFunction>(
    selectedFiles => {
      const acceptedFiles = selectedFiles.filter(file =>
        ACCEPTED_FORMATS.some(format => file.name.toUpperCase().endsWith(format)),
      );
      return {
        filteredFiles: acceptedFiles,
        error:
          acceptedFiles.length < selectedFiles.length ? ERROR_CODES.INVALID_FILE_FORMAT : undefined,
      };
    },
    [documents],
  );

  const removeTooManyFiles = useCallback<FileFilterFunction>(
    selectedFiles => {
      const maxNumberOfFilesToUpload = MAX_NUMBER_OF_FILES - documents.length;
      if (selectedFiles.length > maxNumberOfFilesToUpload) {
        logEvent(ANALYTICS_EVENTS.maxDocumentsAllowedReached, {
          numberOfDocumentsForUpload: selectedFiles.length,
        });
        return {
          filteredFiles: selectedFiles.slice(0, maxNumberOfFilesToUpload),
          error: ERROR_CODES.MAX_NUM_FILES_EXCEEDED,
        };
      }
      return { filteredFiles: selectedFiles };
    },
    [documents],
  );

  const uploadFiles = useCallback(
    (selectedFiles: File[]) => {
      setErrors(null);

      const { files: validSelectedFiles, errors } = [
        removeDuplicateFiles,
        removeInvalidFileFormats,
        removeTooManyFiles,
      ].reduce<{ files: File[]; errors: string[] }>(
        ({ files, errors }, filterFunc: FileFilterFunction) => {
          const { filteredFiles, error } = filterFunc(files);
          return { files: filteredFiles, errors: error ? [...errors, error] : errors };
        },
        { files: selectedFiles, errors: [] },
      );

      if (errors.length) {
        setErrors(errors);
      }

      if (validSelectedFiles.length) {
        onUpload(validSelectedFiles);
      }
    },
    [onUpload],
  );

  const canUploadMoreFiles = useMemo(() => documents.length < MAX_NUMBER_OF_FILES, [documents]);

  const onSelectFile = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedFiles = Array.from((e.target as HTMLInputElement).files || []);
    uploadFiles(selectedFiles);
  };

  return (
    <UploaderContainer
      onDropFiles={uploadFiles}
      disabled={!canUploadMoreFiles}
      dragOverStyle={{
        backgroundColor: designContext
          .get<DesignTokens.Color>('colors/backgrounds/accent-inline')
          .toString(),
      }}
    >
      <IconUpload colorToken={errors ? 'colors/text/neutral' : 'colors/text/default'} />
      <UploadAreaLabel>
        <Typography
          colorToken="colors/text/neutral"
          textToken="text-style/text/labels/body/regular"
        >
          {t(`${tPrefix}.upload_area.title.before_link`)}&nbsp;
          <LinkWithoutHref
            onPress={openFileBrowser}
            disabled={!canUploadMoreFiles}
            UNSAFE_props={{ 'data-testid': 'file-browser-link' }}
          >
            {t(`${tPrefix}.upload_area.title.link_text`)}
          </LinkWithoutHref>
        </Typography>

        <Typography
          colorToken="colors/text/neutral"
          textToken="text-style/text/labels/micro/regular"
        >
          {t(`${tPrefix}.upload_area.description`, {
            acceptedFileFormats: ACCEPTED_FORMATS.join(' '),
            maxFileSizeMB: `${MAX_FILE_SIZE_IN_MB}`,
          })}
        </Typography>
      </UploadAreaLabel>
      <input
        data-testid="file-browser-input"
        type="file"
        style={{ display: 'none' }}
        ref={fileInputRef}
        // @ts-expect-error: FIXME
        onChange={onSelectFile}
        multiple
        accept={ACCEPTED_FORMATS.map(format => format.toLowerCase()).join(',')}
        disabled={!canUploadMoreFiles}
        {...generateCyElemId(cyElementId, window)}
      />
    </UploaderContainer>
  );
};
